import {
  AlertTriangle,
  Check,
  Flag,
  MessageSquare,
  Paperclip
} from "lucide-react";
import { memo, type FC } from "react";

import type { OutgoingIntakeFile } from "@/api/types/outgoingIntake";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import { Chip } from "../../components/Chip";
import type { Decision, ReviewRow } from "../useSubmissionReview";

const COLS =
  "grid grid-cols-[28px_minmax(0,0.9fr)_minmax(0,1.5fr)_126px] gap-3";

const DecisionControl: FC<{
  decision: Decision;
  label: string;
  disabled: boolean;
  onChange: (d: Decision) => void;
}> = ({ decision, label, disabled, onChange }) => (
  <div
    role="radiogroup"
    aria-label={`Decision for ${label}`}
    className="inline-flex h-6 items-center rounded-md border border-border p-px"
  >
    <button
      type="button"
      role="radio"
      aria-checked={decision === "accepted"}
      disabled={disabled}
      onClick={() => onChange(decision === "accepted" ? "pending" : "accepted")}
      className={cn(
        "inline-flex h-5 items-center gap-1 rounded-md px-2 text-xs font-medium disabled:cursor-default",
        decision === "accepted"
          ? "bg-success-soft text-success"
          : "text-muted-foreground enabled:hover:bg-muted enabled:hover:text-foreground"
      )}
    >
      <Check className="size-3" /> Accept
    </button>
    <button
      type="button"
      role="radio"
      aria-checked={decision === "rejected"}
      disabled={disabled}
      onClick={() => onChange(decision === "rejected" ? "pending" : "rejected")}
      className={cn(
        "inline-flex h-5 items-center gap-1 rounded-md px-2 text-xs font-medium disabled:cursor-default",
        decision === "rejected"
          ? "bg-warn-soft text-warn"
          : "text-muted-foreground enabled:hover:bg-muted enabled:hover:text-foreground"
      )}
    >
      <Flag className="size-3" /> Flag
    </button>
  </div>
);

const FileLinks: FC<{
  files: OutgoingIntakeFile[];
  onOpen: (f: OutgoingIntakeFile) => void;
  struck?: boolean;
}> = ({ files, onOpen, struck }) => (
  <span className="flex min-w-0 flex-col gap-0.5">
    {files.map((f) => (
      <button
        key={f.file_id}
        type="button"
        onClick={() => onOpen(f)}
        className={cn(
          "inline-flex min-w-0 items-center gap-1.5 text-left hover:text-primary",
          struck ? "text-muted-foreground line-through" : "text-foreground"
        )}
      >
        <Paperclip className="size-3 shrink-0 text-muted-foreground" />
        <span className="truncate underline decoration-border underline-offset-2">
          {f.filename}
        </span>
      </button>
    ))}
  </span>
);

type RowProps = {
  row: ReviewRow;
  showStep: boolean;
  selected: boolean;
  locked: boolean;
  clientFirstName: string | null;
  onSelect: (row: ReviewRow) => void;
  onDecision: (key: string, d: Decision) => void;
  onNote: (key: string, note: string) => void;
  onOpenFile: (f: OutgoingIntakeFile) => void;
};

const AnswerRow: FC<RowProps> = memo(
  ({
    row,
    showStep,
    selected,
    locked,
    clientFirstName,
    onSelect,
    onDecision,
    onNote,
    onOpenFile
  }) => {
    const isFile = row.node.question_type === "file";
    return (
      <div
        data-answer-key={row.key}
        onClick={() => onSelect(row)}
        className={cn(
          "border-b border-border",
          selected &&
            "bg-primary/5 shadow-[inset_2px_0_0_0_var(--color-primary)]"
        )}
      >
        <div className={cn(COLS, "items-start px-3 py-1.5 text-xs")}>
          <span className="pt-1 text-right text-muted-foreground tabular-nums">
            {showStep ? row.step : ""}
          </span>
          <span className="flex min-w-0 flex-col pt-1">
            {row.context ? (
              <span className="truncate text-xs text-fg-3">{row.context}</span>
            ) : null}
            <span className="text-muted-foreground">{row.label}</span>
          </span>
          <div className="flex min-w-0 flex-col gap-1 pt-1">
            {isFile && row.files.length ? (
              <FileLinks files={row.files} onOpen={onOpenFile} />
            ) : row.display ? (
              <span className="break-words text-foreground">{row.display}</span>
            ) : (
              <span className="text-muted-foreground italic">No answer</span>
            )}
            {row.changed && row.previousDisplay !== null ? (
              <span className="inline-flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                <Chip tone="primary">Changed</Chip>
                {isFile && row.previousFiles.length ? (
                  <>
                    was{" "}
                    <FileLinks
                      files={row.previousFiles}
                      onOpen={onOpenFile}
                      struck
                    />
                  </>
                ) : row.previousDisplay ? (
                  <>
                    was{" "}
                    <span className="line-through">{row.previousDisplay}</span>
                  </>
                ) : (
                  "new answer"
                )}
              </span>
            ) : null}
            {row.attention ? (
              <span className="inline-flex items-center gap-1 text-xs text-destructive">
                <AlertTriangle className="size-3 shrink-0" />
                AI check: {row.attention}
              </span>
            ) : null}
            {row.comment ? (
              <span className="inline-flex items-start gap-1 text-xs text-muted-foreground">
                <MessageSquare className="mt-px size-3 shrink-0" />
                {row.comment}
              </span>
            ) : null}
          </div>
          <div
            className="flex items-center gap-1.5"
            onClick={(e) => e.stopPropagation()}
          >
            <DecisionControl
              decision={row.decision}
              label={row.label}
              disabled={locked}
              onChange={(d) => onDecision(row.key, d)}
            />
          </div>
        </div>
        {row.decision === "rejected" ? (
          <div
            className={cn(COLS, "px-3 pb-2")}
            onClick={(e) => e.stopPropagation()}
          >
            <span />
            <div className="col-span-3 flex items-center gap-2">
              <Input
                value={row.note}
                readOnly={locked}
                onChange={(e) => onNote(row.key, e.target.value)}
                placeholder={`What should ${
                  clientFirstName ?? "your client"
                } fix in “${
                  row.label
                }”? They'll see this note when you request changes.`}
                aria-label={`Note for ${row.label}`}
                className="h-7 text-xs"
              />
            </div>
          </div>
        ) : null}
      </div>
    );
  }
);
AnswerRow.displayName = "AnswerRow";

type Props = Omit<RowProps, "row" | "selected" | "showStep"> & {
  rows: ReviewRow[];
  selectedKey: string | null;
  emptyLabel: string;
};

/** The review checklist: one row per answer, decisions inline. */
export const ReviewTable: FC<Props> = ({
  rows,
  selectedKey,
  emptyLabel,
  ...rest
}) => (
  <div
    role="table"
    className="min-h-0 flex-1 overflow-y-auto [scrollbar-width:thin]"
  >
    <div
      role="row"
      className={cn(
        COLS,
        "sticky top-0 z-[1] h-8 items-center border-b border-border bg-muted px-3 text-xs font-semibold tracking-wide text-muted-foreground uppercase"
      )}
    >
      <span className="text-right">#</span>
      <span>Question</span>
      <span>Answer</span>
      <span>Decision</span>
    </div>
    {rows.length ? (
      rows.map((r, i) => (
        <AnswerRow
          key={r.key}
          row={r}
          showStep={i === 0 || rows[i - 1].stepNodeId !== r.stepNodeId}
          selected={r.key === selectedKey}
          {...rest}
        />
      ))
    ) : (
      <div className="px-3 py-8 text-center text-xs text-muted-foreground">
        {emptyLabel}
      </div>
    )}
  </div>
);
